/**
 * Cloud auth — email + password sign-in/sign-up, and session bootstrap.
 *
 * `initCloudAuth()` is the entry point: it checks for an existing Supabase
 * session, shows the login overlay if none, and otherwise hands control to
 * `onSignedIn` so the app can render its main shell.
 */
import { sb, setCloudUser } from "./supabase";

type SignedInHandler = (userEmail: string) => Promise<void> | void;

let _onSignedIn: SignedInHandler | null = null;

/** Inject the auth-gate overlay HTML into the document. Idempotent. */
function ensureAuthGate() {
  if (document.getElementById("authGate")) return;
  const overlay = document.createElement("div");
  overlay.id = "authGate";
  overlay.className =
    "fixed inset-0 z-[80] hidden bg-[var(--bg)] flex items-center justify-center p-4";
  overlay.innerHTML = `
    <div class="panel p-6 w-full max-w-sm shadow-2xl">
      <div class="flex flex-col items-center gap-2 mb-4">
        <div class="logo-area flex items-center">
          <img src="/logo.png" alt="" class="h-24 w-auto shrink-0"
               onerror="this.style.display='none'; this.parentElement.nextElementSibling.style.display='flex'">
          <img src="/logo2.png" alt="Enigma Ledger" class="h-44 w-auto shrink-0 -ml-10">
        </div>
        <div class="hidden items-center gap-3" style="display:none">
          <div class="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-500 to-cyan-400 grid place-items-center font-bold text-white text-lg">E</div>
          <div class="font-semibold text-lg leading-tight">Enigma Ledger</div>
        </div>
      </div>
      <div id="authError" class="hidden panel-2 !border-rose-500/50 text-rose-300 p-2 text-xs mb-3"></div>
      <form id="authForm" class="space-y-3" onsubmit="return false;">
        <div>
          <label class="label">Email</label>
          <input id="authEmail" type="email" class="input" autocomplete="email" required />
        </div>
        <div>
          <label class="label">Password</label>
          <input id="authPassword" type="password" class="input" autocomplete="current-password" required minlength="6" />
        </div>
        <div class="flex gap-2">
          <button type="button" class="btn btn-primary flex-1" id="btnSignIn">Sign in</button>
          <button type="button" class="btn btn-ghost flex-1" id="btnSignUp">Create account</button>
        </div>
        <div class="text-center pt-1">
          <button type="button" class="text-xs text-cyan-300 hover:underline" id="btnForgot">Forgot password?</button>
        </div>
        <div class="text-xs text-[var(--muted)] text-center pt-1">
          New here? Click <b>Create account</b>. You'll be signed in immediately if email confirmation is off in your Supabase project.
        </div>
      </form>
      <form id="authRecoveryForm" class="space-y-3 hidden" onsubmit="return false;">
        <div class="text-sm">You're resetting your password. Enter a new one below.</div>
        <div>
          <label class="label">New password</label>
          <div class="relative">
            <input id="authNewPassword" type="password" class="input pr-10" autocomplete="new-password" required minlength="6" />
            <button type="button" id="btnTogglePw1" class="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--muted)] hover:text-white p-1" title="Show password" aria-label="Show password">
              <svg id="iconPw1Show" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z"/><circle cx="12" cy="12" r="3"/></svg>
              <svg id="iconPw1Hide" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:none"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-10-8-10-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 10 8 10 8a18.5 18.5 0 0 1-2.16 3.19"/><path d="M14.12 14.12a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
            </button>
          </div>
        </div>
        <div>
          <label class="label">Confirm new password</label>
          <div class="relative">
            <input id="authNewPassword2" type="password" class="input pr-10" autocomplete="new-password" required minlength="6" />
            <button type="button" id="btnTogglePw2" class="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--muted)] hover:text-white p-1" title="Show password" aria-label="Show password">
              <svg id="iconPw2Show" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z"/><circle cx="12" cy="12" r="3"/></svg>
              <svg id="iconPw2Hide" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:none"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-10-8-10-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 10 8 10 8a18.5 18.5 0 0 1-2.16 3.19"/><path d="M14.12 14.12a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
            </button>
          </div>
        </div>
        <button type="button" class="btn btn-primary w-full" id="btnSetPassword">Set new password</button>
      </form>
    </div>`;
  document.body.appendChild(overlay);

  document.getElementById("btnSignIn")!.addEventListener("click", cloudSignIn);
  document.getElementById("btnSignUp")!.addEventListener("click", cloudSignUp);
  document.getElementById("btnForgot")!.addEventListener("click", cloudForgotPassword);
  document.getElementById("btnSetPassword")!.addEventListener("click", cloudSetNewPassword);
  document.getElementById("btnTogglePw1")!.addEventListener("click", () => togglePasswordVisibility("authNewPassword", "iconPw1Show", "iconPw1Hide"));
  document.getElementById("btnTogglePw2")!.addEventListener("click", () => togglePasswordVisibility("authNewPassword2", "iconPw2Show", "iconPw2Hide"));
}

/** Flip a password input between type=password and type=text, swapping the
 *  eye / eye-slash icons in lockstep. */
function togglePasswordVisibility(inputId: string, showIconId: string, hideIconId: string): void {
  const input = document.getElementById(inputId) as HTMLInputElement | null;
  const show = document.getElementById(showIconId) as SVGElement | null;
  const hide = document.getElementById(hideIconId) as SVGElement | null;
  if (!input || !show || !hide) return;
  const willShow = input.type === "password";
  input.type = willShow ? "text" : "password";
  show.style.display = willShow ? "none" : "";
  hide.style.display = willShow ? "" : "none";
}

/** Swap the auth gate between the normal sign-in form and the new-password
 *  recovery form. Supabase fires PASSWORD_RECOVERY on app load when the user
 *  arrives back from a reset-password email link. */
function setAuthMode(mode: "signin" | "recovery"): void {
  const main = document.getElementById("authForm");
  const recov = document.getElementById("authRecoveryForm");
  if (!main || !recov) return;
  if (mode === "recovery") {
    main.classList.add("hidden");
    recov.classList.remove("hidden");
    setTimeout(() => document.getElementById("authNewPassword")?.focus(), 0);
  } else {
    main.classList.remove("hidden");
    recov.classList.add("hidden");
  }
}

function showAuthError(msg: string) {
  const el = document.getElementById("authError");
  if (!el) return;
  el.textContent = msg;
  el.classList.remove("hidden");
}
function hideAuthError() {
  document.getElementById("authError")?.classList.add("hidden");
}
function setAuthGate(visible: boolean) {
  const gate = document.getElementById("authGate");
  if (!gate) return;
  gate.classList.toggle("hidden", !visible);
  gate.classList.toggle("flex", visible);
  if (visible) {
    setTimeout(() => document.getElementById("authEmail")?.focus(), 0);
  }
}

export async function cloudSignIn() {
  hideAuthError();
  const email = (document.getElementById("authEmail") as HTMLInputElement)?.value.trim();
  const password = (document.getElementById("authPassword") as HTMLInputElement)?.value;
  if (!email || !password) return showAuthError("Email and password are required.");
  const { data, error } = await sb.auth.signInWithPassword({ email, password });
  if (error) return showAuthError(error.message);
  setCloudUser(data.user);
  setAuthGate(false);
  await afterCloudAuth();
}

export async function cloudSignUp() {
  hideAuthError();
  const email = (document.getElementById("authEmail") as HTMLInputElement)?.value.trim();
  const password = (document.getElementById("authPassword") as HTMLInputElement)?.value;
  if (!email || !password) return showAuthError("Email and password are required.");
  if (password.length < 6) return showAuthError("Password must be at least 6 characters.");
  const { data, error } = await sb.auth.signUp({ email, password });
  if (error) return showAuthError(error.message);
  if (data.user && data.session) {
    setCloudUser(data.user);
    setAuthGate(false);
    await afterCloudAuth();
  } else {
    showAuthError("Account created. Check your email for the confirmation link, then sign in.");
  }
}

export async function cloudSignOut() {
  await sb.auth.signOut();
  setCloudUser(null);
  // Hard reload to drop any in-memory state.
  location.reload();
}

/** "Forgot password?" — email the user a reset link. They click the link in
 *  their email, get redirected back to this app, and Supabase fires the
 *  PASSWORD_RECOVERY event which we handle below in onAuthStateChange. */
export async function cloudForgotPassword() {
  hideAuthError();
  const email = (document.getElementById("authEmail") as HTMLInputElement)?.value.trim();
  if (!email) return showAuthError("Type your email in the field above, then click Forgot password? again.");
  const redirectTo = window.location.origin + window.location.pathname;
  const { error } = await sb.auth.resetPasswordForEmail(email, { redirectTo });
  if (error) return showAuthError(error.message);
  showAuthError("Password reset email sent. Check your inbox and click the link to set a new password.");
  const el = document.getElementById("authError");
  if (el) {
    el.classList.remove("text-rose-300", "!border-rose-500/50");
    el.classList.add("text-emerald-300", "!border-emerald-500/50");
  }
}

/** Called from the recovery-mode form to commit the new password after the
 *  user clicked their email link and was returned to the app. */
export async function cloudSetNewPassword() {
  hideAuthError();
  const password  = (document.getElementById("authNewPassword")  as HTMLInputElement)?.value;
  const password2 = (document.getElementById("authNewPassword2") as HTMLInputElement)?.value;
  if (!password || password.length < 6) return showAuthError("Password must be at least 6 characters.");
  if (password !== password2) return showAuthError("Passwords don't match - re-type and try again.");
  const { error } = await sb.auth.updateUser({ password });
  if (error) return showAuthError(error.message);
  setAuthMode("signin");
  setAuthGate(false);
  await afterCloudAuth();
}

async function afterCloudAuth() {
  const { data: { user } } = await sb.auth.getUser();
  if (!user) {
    setAuthGate(true);
    return;
  }
  setCloudUser(user);
  if (_onSignedIn) await _onSignedIn(user.email || "");
}

export async function initCloudAuth(onSignedIn: SignedInHandler) {
  _onSignedIn = onSignedIn;
  ensureAuthGate();

  const { data: { session } } = await sb.auth.getSession();

  const isRecovery = sessionStorage.getItem("_enigma_recovery") === "1";
  if (isRecovery) {
    sessionStorage.removeItem("_enigma_recovery");
    if (session?.user) setCloudUser(session.user);
    setAuthMode("recovery");
    setAuthGate(true);
    return;
  }

  if (session?.user) {
    setCloudUser(session.user);
    setAuthGate(false);
    await afterCloudAuth();
  } else {
    setAuthGate(true);
  }

  sb.auth.onAuthStateChange((event, session) => {
    if (event === "PASSWORD_RECOVERY") {
      if (session?.user) setCloudUser(session.user);
      setAuthMode("recovery");
      setAuthGate(true);
      return;
    }
    if ((event === "SIGNED_IN" || event === "TOKEN_REFRESHED") && session?.user) {
      setCloudUser(session.user);
      setAuthGate(false);
      afterCloudAuth();
    } else if (event === "SIGNED_OUT") {
      setCloudUser(null);
      setAuthGate(true);
    }
  });
}
